import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

@observer
class UploadExternalResults extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // Form state
            raceName: '',
            raceClassName: '',
            raceDate: '',
            distance: '',
            location: '',
            country: '',
            csvContent: '',
            notes: '',
            // UI state
            step: 'upload', // 'upload', 'review', 'complete'
            error: null,
            success: null,
            isSubmitting: false,
            // Preview/review state
            previewData: null,
            reviewResults: [],
            // User search state
            searchingIndex: null,
            userSearchQuery: '',
            userSearchResults: [],
            isSearchingUsers: false,
        };
        this.searchTimeout = null;
    }

    handleInputChange = (field) => (e) => {
        this.setState({ [field]: e.target.value, error: null });
    }

    handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            this.setState({ csvContent: event.target.result, error: null });
        };
        reader.onerror = () => {
            this.setState({ error: 'Failed to read file' });
        };
        reader.readAsText(file);
    }

    handleParseCSV = async (e) => {
        e.preventDefault();
        const { externalRaceResultStore } = this.props.stores;
        const { raceName, raceClassName, raceDate, distance, location, country, csvContent, notes } = this.state;

        if (!raceName || !raceDate || !csvContent) {
            this.setState({ error: 'Race name, date, and CSV content are required' });
            return;
        }

        this.setState({ isSubmitting: true, error: null, success: null });

        try {
            const response = await externalRaceResultStore.uploadCSV({
                raceName,
                raceClassName,
                raceDate,
                distance: distance ? parseInt(distance, 10) : null,
                location,
                country,
                csvContent,
                notes,
            });

            if (response.data && response.data.preview) {
                // Got preview data - show review step
                this.setState({
                    step: 'review',
                    previewData: response.data,
                    reviewResults: response.data.results || [],
                    isSubmitting: false,
                });
            } else {
                this.setState({ error: 'Unexpected response from server' });
            }
        } catch (error) {
            this.setState({
                error: error.message || 'Failed to parse CSV',
                isSubmitting: false,
            });
        }
    }

    handleUserSelect = (resultIndex, user) => {
        const { reviewResults } = this.state;
        const updated = [...reviewResults];
        updated[resultIndex] = {
            ...updated[resultIndex],
            selectedUserId: user ? user.id : null,
            matchedUser: user ? {
                id: user.id,
                firstname: user.firstname,
                lastname: user.lastname,
                email: user.email,
                cellphone: user.cellphone,
            } : null,
            matchStatus: user ? 'manual' : 'none',
        };
        this.setState({
            reviewResults: updated,
            searchingIndex: null,
            userSearchQuery: '',
            userSearchResults: [],
        });
    }

    handleClearUser = (resultIndex) => {
        const { reviewResults } = this.state;
        const updated = [...reviewResults];
        updated[resultIndex] = {
            ...updated[resultIndex],
            selectedUserId: null,
            matchedUser: null,
            matchStatus: 'none',
        };
        this.setState({ reviewResults: updated });
    }

    handleOpenUserSearch = (resultIndex) => {
        const { reviewResults } = this.state;
        const result = reviewResults[resultIndex];
        this.setState({
            searchingIndex: resultIndex,
            userSearchQuery: `${result.firstname} ${result.lastname}`.trim(),
            userSearchResults: result.suggestedMatches || [],
        });
    }

    handleCloseUserSearch = () => {
        this.setState({
            searchingIndex: null,
            userSearchQuery: '',
            userSearchResults: [],
        });
    }

    handleUserSearchChange = (e) => {
        const query = e.target.value;
        this.setState({ userSearchQuery: query });

        // Debounce search
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        if (query.length >= 2) {
            this.searchTimeout = setTimeout(() => {
                this.searchUsers(query);
            }, 300);
        } else {
            this.setState({ userSearchResults: [] });
        }
    }

    searchUsers = async (query) => {
        this.setState({ isSearchingUsers: true });
        try {
            const response = await util.fetchApi('/api/users/search-all', { method: 'GET' }, { search: query, limit: 10 });
            if (response.status === 200) {
                this.setState({
                    userSearchResults: (response.data || []).map(u => ({
                        id: u.id,
                        firstname: u.firstname,
                        lastname: u.lastname,
                        email: u.email,
                        cellphone: u.cellphone,
                        score: 100,
                    })),
                });
            }
        } catch (error) {
            console.error('User search error:', error);
        } finally {
            this.setState({ isSearchingUsers: false });
        }
    }

    handleConfirm = async () => {
        const { externalRaceResultStore } = this.props.stores;
        const { previewData, reviewResults } = this.state;

        this.setState({ isSubmitting: true, error: null });

        try {
            const response = await externalRaceResultStore.confirmCSV({
                raceInfo: previewData.raceInfo,
                results: reviewResults,
            });

            if (response.status === 201 || response.status === 200) {
                this.setState({
                    step: 'complete',
                    success: `Successfully saved ${response.meta?.totalResults || 0} results (${response.meta?.resultsWithUserId || 0} with user IDs)`,
                    isSubmitting: false,
                });
            } else {
                this.setState({
                    error: response.message || 'Failed to save results',
                    isSubmitting: false,
                });
            }
        } catch (error) {
            this.setState({
                error: error.message || 'Failed to save results',
                isSubmitting: false,
            });
        }
    }

    handleProcessRatings = async () => {
        const { externalRaceResultStore } = this.props.stores;
        const uploadResult = externalRaceResultStore.uploadResult;

        if (!uploadResult?.data?.id) {
            this.setState({ error: 'No uploaded race to process' });
            return;
        }

        this.setState({ isSubmitting: true, error: null });

        try {
            await externalRaceResultStore.processForRatings(uploadResult.data.id);
            this.setState({
                success: 'Ratings processed successfully',
                isSubmitting: false,
            });

            // Refresh leaderboard
            const { musherRatingStore } = this.props.stores;
            await musherRatingStore.loadLeaderboard(musherRatingStore.selectedYear);

            // Call onSuccess callback
            if (this.props.onSuccess) {
                this.props.onSuccess();
            }
        } catch (error) {
            this.setState({
                error: error.message || 'Failed to process ratings',
                isSubmitting: false,
            });
        }
    }

    handleBackToUpload = () => {
        this.setState({
            step: 'upload',
            previewData: null,
            reviewResults: [],
            error: null,
            success: null,
        });
    }

    handleReset = () => {
        const { externalRaceResultStore } = this.props.stores;
        externalRaceResultStore.setUploadResult(null);
        this.setState({
            raceName: '',
            raceClassName: '',
            raceDate: '',
            distance: '',
            location: '',
            country: '',
            csvContent: '',
            notes: '',
            step: 'upload',
            error: null,
            success: null,
            previewData: null,
            reviewResults: [],
        });
    }

    getMatchStatusBadge(status) {
        const { darkmode } = this.props;
        switch (status) {
            case 'exact':
                return <span class="badge bg-success">Exact Match</span>;
            case 'fuzzy':
                return <span class="badge bg-warning text-dark">Fuzzy Match</span>;
            case 'manual':
                return <span class="badge bg-info">Manual</span>;
            default:
                return <span class={`badge ${darkmode ? 'bg-secondary' : 'bg-light text-dark'}`}>No Match</span>;
        }
    }

    renderUploadForm() {
        const { darkmode } = this.props;
        const { raceName, raceClassName, raceDate, distance, location, country, csvContent, notes, error, isSubmitting } = this.state;

        return (
            <div class={`card ${darkmode ? 'bg-dark text-white' : ''}`}>
                <div class="card-header">
                    <h5 class="mb-0">
                        <i class="fa-solid fa-upload me-2"></i>
                        <Text id="musherRating.uploadExternalResults">Upload External Race Results</Text>
                    </h5>
                </div>
                <div class="card-body">
                    {error && (
                        <div class="alert alert-danger" role="alert">
                            <i class="fa-solid fa-exclamation-triangle me-2"></i>
                            {error}
                        </div>
                    )}

                    <form onSubmit={this.handleParseCSV}>
                        <div class="row">
                            <div class="col-12 col-md-6">
                                <div class="form-group mb-3">
                                    <label><Text id="musherRating.raceName">Race Name</Text> *</label>
                                    <input
                                        type="text"
                                        class={`form-control ${darkmode ? 'bg-dark text-white border-secondary' : ''}`}
                                        value={raceName}
                                        onInput={this.handleInputChange('raceName')}
                                        placeholder="e.g., Finnmarkslopet 2024"
                                        required
                                    />
                                </div>
                            </div>
                            <div class="col-12 col-md-6">
                                <div class="form-group mb-3">
                                    <label><Text id="musherRating.raceClassName">Race Class</Text></label>
                                    <input
                                        type="text"
                                        class={`form-control ${darkmode ? 'bg-dark text-white border-secondary' : ''}`}
                                        value={raceClassName}
                                        onInput={this.handleInputChange('raceClassName')}
                                        placeholder="e.g., FL-500"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-12 col-md-4">
                                <div class="form-group mb-3">
                                    <label><Text id="musherRating.raceDate">Race Date</Text> *</label>
                                    <input
                                        type="date"
                                        class={`form-control ${darkmode ? 'bg-dark text-white border-secondary' : ''}`}
                                        value={raceDate}
                                        onInput={this.handleInputChange('raceDate')}
                                        required
                                    />
                                </div>
                            </div>
                            <div class="col-12 col-md-4">
                                <div class="form-group mb-3">
                                    <label><Text id="musherRating.distance">Distance (km)</Text></label>
                                    <input
                                        type="number"
                                        class={`form-control ${darkmode ? 'bg-dark text-white border-secondary' : ''}`}
                                        value={distance}
                                        onInput={this.handleInputChange('distance')}
                                        placeholder="e.g., 500"
                                    />
                                </div>
                            </div>
                            <div class="col-12 col-md-4">
                                <div class="form-group mb-3">
                                    <label><Text id="musherRating.location">Location</Text></label>
                                    <input
                                        type="text"
                                        class={`form-control ${darkmode ? 'bg-dark text-white border-secondary' : ''}`}
                                        value={location}
                                        onInput={this.handleInputChange('location')}
                                        placeholder="e.g., Alta"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="form-group mb-3">
                            <label><Text id="musherRating.csvFile">CSV File</Text></label>
                            <input
                                type="file"
                                class={`form-control ${darkmode ? 'bg-dark text-white border-secondary' : ''}`}
                                accept=".csv,.txt"
                                onChange={this.handleFileUpload}
                            />
                            <small class="form-text text-muted">
                                Or paste CSV content below
                            </small>
                        </div>

                        <div class="form-group mb-3">
                            <label><Text id="musherRating.csvContent">CSV Content</Text> *</label>
                            <textarea
                                class={`form-control font-monospace ${darkmode ? 'bg-dark text-white border-secondary' : ''}`}
                                rows="8"
                                value={csvContent}
                                onInput={this.handleInputChange('csvContent')}
                                placeholder="position&#9;firstname&#9;lastname&#9;status&#10;1&#9;John&#9;Smith&#9;OK&#10;2&#9;Jane&#9;Doe&#9;OK"
                                style={{ fontFamily: 'monospace', fontSize: '12px' }}
                            />
                            <small class="form-text text-muted">
                                Format: Tab or semicolon separated. Required: position, firstname, lastname. Optional: user, status, finishTime.
                            </small>
                        </div>

                        <div class="form-group mb-3">
                            <label><Text id="musherRating.notes">Notes</Text></label>
                            <textarea
                                class={`form-control ${darkmode ? 'bg-dark text-white border-secondary' : ''}`}
                                rows="2"
                                value={notes}
                                onInput={this.handleInputChange('notes')}
                                placeholder="Any additional notes..."
                            />
                        </div>

                        <button
                            type="submit"
                            class="btn btn-primary"
                            disabled={isSubmitting || !raceName || !raceDate || !csvContent}
                        >
                            {isSubmitting ? (
                                <><i class="fa-solid fa-spinner fa-spin me-2"></i>Parsing...</>
                            ) : (
                                <><i class="fa-solid fa-search me-2"></i>Parse &amp; Match Users</>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    renderReviewStep() {
        const { darkmode } = this.props;
        const { previewData, reviewResults, error, isSubmitting, searchingIndex, userSearchQuery, userSearchResults, isSearchingUsers } = this.state;
        const { externalRaceResultStore } = this.props.stores;
        const meta = externalRaceResultStore.uploadResult?.meta || {};

        const raceInfo = previewData?.raceInfo || {};

        // Calculate current match stats
        const exactMatches = reviewResults.filter(r => r.matchStatus === 'exact').length;
        const fuzzyMatches = reviewResults.filter(r => r.matchStatus === 'fuzzy').length;
        const manualMatches = reviewResults.filter(r => r.matchStatus === 'manual').length;
        const noMatches = reviewResults.filter(r => r.matchStatus === 'none').length;
        const totalWithUser = reviewResults.filter(r => r.selectedUserId || r.matchedUser).length;

        return (
            <div class={`card ${darkmode ? 'bg-dark text-white' : ''}`}>
                <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="mb-0">
                        <i class="fa-solid fa-users me-2"></i>
                        Review User Matches
                    </h5>
                    <button class="btn btn-sm btn-outline-secondary" onClick={this.handleBackToUpload}>
                        <i class="fa-solid fa-arrow-left me-1"></i> Back
                    </button>
                </div>
                <div class="card-body">
                    {error && (
                        <div class="alert alert-danger" role="alert">
                            <i class="fa-solid fa-exclamation-triangle me-2"></i>
                            {error}
                        </div>
                    )}

                    {/* Race Info Summary */}
                    <div class={`alert ${darkmode ? 'alert-secondary' : 'alert-info'}`}>
                        <strong>{raceInfo.raceName}</strong>
                        {raceInfo.raceClassName && <span class="ms-2">({raceInfo.raceClassName})</span>}
                        <br />
                        <small>
                            Date: {raceInfo.raceDate ? new Date(raceInfo.raceDate).toLocaleDateString() : 'N/A'}
                            {raceInfo.distance && <span class="ms-2">| Distance: {raceInfo.distance} km</span>}
                            {raceInfo.location && <span class="ms-2">| Location: {raceInfo.location}</span>}
                        </small>
                    </div>

                    {/* Match Statistics */}
                    <div class="row mb-3">
                        <div class="col">
                            <div class="d-flex flex-wrap gap-2">
                                <span class="badge bg-success">
                                    <i class="fa-solid fa-check me-1"></i> {exactMatches} Exact
                                </span>
                                <span class="badge bg-warning text-dark">
                                    <i class="fa-solid fa-question me-1"></i> {fuzzyMatches} Fuzzy
                                </span>
                                <span class="badge bg-info">
                                    <i class="fa-solid fa-user me-1"></i> {manualMatches} Manual
                                </span>
                                <span class={`badge ${darkmode ? 'bg-secondary' : 'bg-light text-dark'}`}>
                                    <i class="fa-solid fa-times me-1"></i> {noMatches} No Match
                                </span>
                                <span class="badge bg-primary ms-2">
                                    Total with users: {totalWithUser}/{reviewResults.length}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Results Table */}
                    <div class="table-responsive" style={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
                        <table class={`table table-sm ${darkmode ? 'table-dark' : ''}`}>
                            <thead style={{ position: 'sticky', top: 0 }}>
                                <tr>
                                    <th style={{ width: '50px' }}>Pos</th>
                                    <th>Name (CSV)</th>
                                    <th>Status</th>
                                    <th>Matched User</th>
                                    <th style={{ width: '120px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reviewResults.map((result, idx) => (
                                    <tr key={idx} class={result.matchStatus === 'none' ? (darkmode ? 'table-secondary' : 'table-warning') : ''}>
                                        <td>{result.position}</td>
                                        <td>
                                            <strong>{result.firstname} {result.lastname}</strong>
                                            {result.finishTime && <small class="text-body-secondary ms-2">({result.finishTime})</small>}
                                        </td>
                                        <td>{this.getMatchStatusBadge(result.matchStatus)}</td>
                                        <td>
                                            {searchingIndex === idx ? (
                                                <div class="position-relative">
                                                    <input
                                                        type="text"
                                                        class="form-control form-control-sm"
                                                        value={userSearchQuery}
                                                        onInput={this.handleUserSearchChange}
                                                        placeholder="Search users..."
                                                        autoFocus
                                                    />
                                                    {(userSearchResults.length > 0 || isSearchingUsers) && (
                                                        <div
                                                            class="position-absolute w-100 list-group shadow"
                                                            style={{ zIndex: 1000, maxHeight: '250px', overflowY: 'auto' }}
                                                        >
                                                            {isSearchingUsers && (
                                                                <div class="list-group-item text-center">
                                                                    <i class="fa-solid fa-spinner fa-spin"></i>
                                                                </div>
                                                            )}
                                                            {userSearchResults.map(user => (
                                                                <button
                                                                    type="button"
                                                                    key={user.id}
                                                                    class="list-group-item list-group-item-action"
                                                                    onClick={() => this.handleUserSelect(idx, user)}
                                                                >
                                                                    <div class="d-flex justify-content-between align-items-center">
                                                                        <strong>{user.firstname} {user.lastname}</strong>
                                                                        {user.score && <span class="badge bg-info">{user.score}%</span>}
                                                                    </div>
                                                                    <small class="text-body-secondary">
                                                                        {user.email}{user.cellphone && <span class="ms-2">{user.cellphone}</span>} (ID: {user.id})
                                                                    </small>
                                                                </button>
                                                            ))}
                                                            <button
                                                                type="button"
                                                                class="list-group-item list-group-item-action text-body-secondary"
                                                                onClick={() => this.handleUserSelect(idx, null)}
                                                            >
                                                                <i class="fa-solid fa-times me-1"></i> No user (skip rating)
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                result.matchedUser ? (
                                                    <div>
                                                        <strong>{result.matchedUser.firstname} {result.matchedUser.lastname}</strong>
                                                        <br />
                                                        <small class="text-body-secondary">{result.matchedUser.email}{result.matchedUser.cellphone && <span class="ms-2">{result.matchedUser.cellphone}</span>} (ID: {result.matchedUser.id})</small>
                                                    </div>
                                                ) : (
                                                    <span class="text-body-secondary">-</span>
                                                )
                                            )}
                                        </td>
                                        <td>
                                            {searchingIndex === idx ? (
                                                <button
                                                    class="btn btn-sm btn-outline-secondary"
                                                    onClick={this.handleCloseUserSearch}
                                                >
                                                    <i class="fa-solid fa-times"></i>
                                                </button>
                                            ) : (
                                                <div class="btn-group btn-group-sm">
                                                    <button
                                                        class="btn btn-outline-primary"
                                                        onClick={() => this.handleOpenUserSearch(idx)}
                                                        title="Search for user"
                                                    >
                                                        <i class="fa-solid fa-search"></i>
                                                    </button>
                                                    {result.matchedUser && (
                                                        <button
                                                            class="btn btn-outline-danger"
                                                            onClick={() => this.handleClearUser(idx)}
                                                            title="Clear user"
                                                        >
                                                            <i class="fa-solid fa-times"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Action Buttons */}
                    <div class="mt-3 d-flex justify-content-between">
                        <button class="btn btn-outline-secondary" onClick={this.handleBackToUpload}>
                            <i class="fa-solid fa-arrow-left me-1"></i> Back to Edit
                        </button>
                        <button
                            class="btn btn-success"
                            onClick={this.handleConfirm}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <><i class="fa-solid fa-spinner fa-spin me-2"></i>Saving...</>
                            ) : (
                                <><i class="fa-solid fa-check me-2"></i>Confirm &amp; Save ({totalWithUser} with users)</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderCompleteStep() {
        const { darkmode, stores } = this.props;
        const { externalRaceResultStore } = stores;
        const { success, error, isSubmitting } = this.state;
        const uploadResult = externalRaceResultStore.uploadResult;

        return (
            <div class={`card ${darkmode ? 'bg-dark text-white' : ''}`}>
                <div class="card-header">
                    <h5 class="mb-0">
                        <i class="fa-solid fa-check-circle me-2 text-success"></i>
                        Upload Complete
                    </h5>
                </div>
                <div class="card-body">
                    {error && (
                        <div class="alert alert-danger" role="alert">
                            <i class="fa-solid fa-exclamation-triangle me-2"></i>
                            {error}
                        </div>
                    )}
                    {success && (
                        <div class="alert alert-success" role="alert">
                            <i class="fa-solid fa-check-circle me-2"></i>
                            {success}
                        </div>
                    )}

                    {uploadResult?.data && (
                        <div class={`alert ${darkmode ? 'alert-secondary' : 'alert-info'}`}>
                            <h6 class="alert-heading">
                                <i class="fa-solid fa-info-circle me-2"></i>
                                {uploadResult.data.raceName}
                            </h6>
                            <p class="mb-2">
                                <strong>{uploadResult.meta?.totalResults || 0}</strong> results saved.
                                <br />
                                <strong>{uploadResult.meta?.resultsWithUserId || 0}</strong> results have user IDs and will count toward rankings.
                            </p>
                            <hr />
                            <div class="d-flex gap-2">
                                <button
                                    class="btn btn-primary"
                                    onClick={this.handleProcessRatings}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <><i class="fa-solid fa-spinner fa-spin me-2"></i>Processing...</>
                                    ) : (
                                        <><i class="fa-solid fa-calculator me-2"></i>Process for Rankings</>
                                    )}
                                </button>
                                <button class="btn btn-outline-secondary" onClick={this.handleReset}>
                                    <i class="fa-solid fa-plus me-2"></i>Upload Another
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { darkmode, fullWidth } = this.props;
        const { step } = this.state;

        const colClass = fullWidth && step === 'review' ? 'col-12' : 'col-12 col-lg-10 offset-lg-1';

        return (
            <div class="row h-100">
                <div class={colClass}>
                    {step === 'upload' && this.renderUploadForm()}
                    {step === 'review' && this.renderReviewStep()}
                    {step === 'complete' && this.renderCompleteStep()}

                    {/* CSV Format Help - only show on upload step */}
                    {step === 'upload' && (
                        <div class={`card mt-3 ${darkmode ? 'bg-dark text-white' : ''}`}>
                            <div class="card-header">
                                <h6 class="mb-0">
                                    <i class="fa-solid fa-question-circle me-2"></i>
                                    CSV Format Help
                                </h6>
                            </div>
                            <div class="card-body">
                                <p><strong>Required columns:</strong> position, firstname, lastname</p>
                                <p><strong>Optional columns:</strong></p>
                                <ul>
                                    <li><strong>user</strong> - User ID (will skip fuzzy matching if provided)</li>
                                    <li><strong>status</strong> - OK (default), DNF, DNS, or DSQ</li>
                                    <li><strong>finishTime</strong> - Finish time (HH:MM:SS)</li>
                                </ul>
                                <p><strong>Example:</strong></p>
                                <pre class={`p-2 rounded ${darkmode ? 'bg-secondary' : 'bg-light'}`} style={{ fontSize: '11px' }}>
position{'\t'}firstname{'\t'}lastname{'\t'}status{'\n'}
1{'\t'}John{'\t'}Smith{'\t'}OK{'\n'}
2{'\t'}Jane{'\t'}Doe{'\t'}OK{'\n'}
3{'\t'}Bob{'\t'}Johnson{'\t'}DNF
                                </pre>
                                <p class="mb-0 small text-muted">
                                    <i class="fa-solid fa-magic me-1"></i>
                                    The system will automatically try to match names to existing users using fuzzy matching.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default UploadExternalResults;
